import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Users, Wallet, CalendarClock, Briefcase, Link as LinkIcon, Plus } from 'lucide-react';
import R from '../../navigation/opsRoutes';
import {
  listPeople,
  savePerson,
  updatePerson,
  updateProLabore,
  listHeadcountPlan,
  saveHeadcountPlan,
  listGovernancePermissions,
  savePermission,
  RH_COST_CENTERS,
  RH_FINANCIAL_CATEGORIES,
  RH_SCENARIOS,
} from '../../services/governanceHrService';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

const RH_LABELS = {
  pt: {
    tabLabels: { overview: 'Overview', people: 'Pessoas', compensation: 'Pró-labore & Remuneração', 'headcount-plan': 'Planejamento de Pessoal', permissions: 'Permissões de Governança', 'financial-integration': 'Integração Financeira' },
    title: 'RH & Pessoas',
    subtitle: 'Controle interno de equipe, pró-labore, permissões de governança e planejamento financeiro de pessoal.',
    badge: 'Governança Interna',
    loading: 'Carregando módulo de RH...',
  },
  en: {
    tabLabels: { overview: 'Overview', people: 'People', compensation: 'Pro-labore & Compensation', 'headcount-plan': 'Staff Planning', permissions: 'Governance Permissions', 'financial-integration': 'Financial Integration' },
    title: 'HR & People',
    subtitle: 'Internal team control, pro-labore, governance permissions and personnel financial planning.',
    badge: 'Internal Governance',
    loading: 'Loading HR module...',
  }
};

const BRAND_GREEN = '#064e3b';
const BRAND_GOLD = '#b48c2c';
const BRAND_WHITE = '#ffffff';
const BRAND_GREEN_LIGHT = '#d1fae5';

const TAB_ITEMS = [
  { id: 'overview', label: 'Overview', path: R.rhOverview },
  { id: 'people', label: 'Pessoas', path: R.rhPeople },
  { id: 'compensation', label: 'Pró-labore & Remuneração', path: R.rhCompensation },
  { id: 'headcount-plan', label: 'Planejamento de Pessoal', path: R.rhHeadcountPlan },
  { id: 'permissions', label: 'Permissões de Governança', path: R.rhPermissions },
  { id: 'financial-integration', label: 'Integração Financeira', path: R.rhFinancial },
];

const PATH_TO_TAB = TAB_ITEMS.reduce((acc, item) => {
  acc[item.path] = item.id;
  return acc;
}, { [R.rh]: 'overview' });

const currency = (value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value || 0));
const toNumber = (value) => {
  if (value === '' || value === null || value === undefined) return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const EMPTY_PERMISSION_LEVEL = 'Sem acesso';
const GOVERNANCE_AREAS = [
  { key: 'financeiro', label: 'Financeiro' },
  { key: 'investidores', label: 'Investidores' },
  { key: 'marketing', label: 'Marketing' },
  { key: 'comercial', label: 'Comercial' },
  { key: 'projetos', label: 'Projetos' },
  { key: 'rh', label: 'RH' },
  { key: 'governanceAdmin', label: 'Governança Admin' },
];

const PERSON_TYPE_OPTIONS = ['Sócio/Fundador', 'CLT', 'PJ', 'Estagiário', 'Consultor', 'Prestador'];
const STATUS_OPTIONS = ['Planejado', 'Ativo', 'Inativo'];
const REGIME_OPTIONS = ['Pró-labore', 'CLT', 'PJ', 'Bolsa', 'Sem remuneração atual'];
const AREA_OPTIONS = [
  'Governança',
  'Estratégia / Governança',
  'Produto',
  'Tecnologia',
  'IA',
  'Financeiro',
  'Comercial',
  'Marketing',
  'Operações',
];

const makeDefaultPerson = () => ({
  name: '',
  email: '',
  type: 'CLT',
  roleTitle: '',
  primaryArea: 'Produto',
  secondaryAreas: '',
  startDate: '',
  status: 'Planejado',
  regime: 'CLT',
  monthlyGrossCost: '',
  burdenRate: '',
  costCenter: RH_COST_CENTERS[0],
  financialCategory: RH_FINANCIAL_CATEGORIES[0],
  projectionScenario: 'Não definido',
  impactsFinancial: true,
  notes: '',
});

const makeDefaultPlan = () => ({
  plannedRole: '',
  area: 'Produto',
  startMonth: '',
  scenario: 'Não definido',
  monthlyGrossCost: '',
  burdenRate: '',
  costCenter: RH_COST_CENTERS[0],
  financialCategory: RH_FINANCIAL_CATEGORIES[0],
  priority: 'Média',
  dependency: '',
  status: 'Planejado',
  notes: '',
});

const makeDefaultPermission = () => ({
  personId: 1,
  governanceArea: 'financeiro',
  permissionLevel: 'Leitor',
  notes: '',
});

const makeDefaultProLaboreForm = () => ({
  monthlyValue: 0,
  startMonth: '',
  costCenter: 'Administração / Estratégia',
  financialCategory: 'Pró-labore',
  impactsFinancial: true,
  scenario: 'Não definido',
  notes: '',
});

const GovernanceRHPage = () => {
  const { lang } = useGovernanceLang();
  const t = RH_LABELS[lang] || RH_LABELS.pt;
  const location = useLocation();
  const navigate = useNavigate();

  const activeTab = PATH_TO_TAB[location.pathname] || 'overview';

  const [people, setPeople] = useState([]);
  const [headcountPlan, setHeadcountPlan] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showProLaboreForm, setShowProLaboreForm] = useState(false);
  const [showPersonForm, setShowPersonForm] = useState(false);
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [showPermissionForm, setShowPermissionForm] = useState(false);

  const [proLaboreForm, setProLaboreForm] = useState(makeDefaultProLaboreForm());
  const [personForm, setPersonForm] = useState(makeDefaultPerson());
  const [planForm, setPlanForm] = useState(makeDefaultPlan());
  const [permissionForm, setPermissionForm] = useState(makeDefaultPermission());

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [peopleRes, headcountRes, permissionsRes] = await Promise.all([
          listPeople(),
          listHeadcountPlan(),
          listGovernancePermissions(),
        ]);

        const loadedPeople = peopleRes?.data || [];
        const founder = loadedPeople.find((p) => p.id === 1 || p.name === 'Paulo Brant');

        setPeople(loadedPeople);
        setHeadcountPlan(headcountRes?.data || []);
        setPermissions(permissionsRes?.data || []);

        setProLaboreForm({
          monthlyValue: founder?.monthlyGrossCost ?? 0,
          startMonth: founder?.startDate || '',
          costCenter: founder?.costCenter || 'Administração / Estratégia',
          financialCategory: (founder?.financialCategory || '').includes('Pró-labore') ? 'Pró-labore' : (founder?.financialCategory || 'Pró-labore'),
          impactsFinancial: founder?.impactsFinancial !== false,
          scenario: founder?.projectionScenario || 'Não definido',
          notes: founder?.notes || '',
        });

        setPermissionForm((prev) => ({
          ...prev,
          personId: loadedPeople[0]?.id || 1,
        }));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const founder = useMemo(() => people.find((p) => p.id === 1 || p.name === 'Paulo Brant') || null, [people]);

  const activePeople = useMemo(() => people.filter((p) => p.status === 'Ativo'), [people]);

  const metrics = useMemo(() => {
    const proLaboreAtual = toNumber(founder?.monthlyGrossCost);
    const monthlyCurrentCost = activePeople.reduce((acc, p) => acc + toNumber(p.monthlyTotalCost || p.monthlyGrossCost), 0);
    const plannedItems = headcountPlan.filter((h) => h.status === 'Planejado' || h.status === 'Aprovado');
    const plannedMonthlyCost = plannedItems.reduce((acc, item) => acc + toNumber(item.totalMonthlyCost), 0);

    const activeAreas = [...new Set(activePeople.map((p) => p.primaryArea).filter(Boolean))];

    return {
      activePeople: activePeople.length,
      proLaboreAtual,
      monthlyCurrentCost,
      plannedCount: plannedItems.length,
      plannedMonthlyCost,
      coveredAreasText: activeAreas.length > 0 ? activeAreas.join(' · ') : '—',
    };
  }, [activePeople, founder, headcountPlan]);

  const financialRows = useMemo(() => {
    const currentRows = activePeople.map((person) => ({
      key: `person-${person.id}`,
      personOrRole: person.name,
      costCenter: person.costCenter || '',
      financialCategory: person.financialCategory || '',
      projectionScenario: person.projectionScenario || 'Não definido',
      startMonth: person.startDate || '',
      totalMonthlyCost: toNumber(person.monthlyTotalCost || person.monthlyGrossCost),
      annualImpact: toNumber(person.monthlyTotalCost || person.monthlyGrossCost) * 12,
      status: person.status,
    }));

    const plannedRows = headcountPlan.map((item) => ({
      key: `plan-${item.id}`,
      personOrRole: item.plannedRole,
      costCenter: item.costCenter || '',
      financialCategory: item.financialCategory || '',
      projectionScenario: item.scenario || 'Não definido',
      startMonth: item.startMonth || '',
      totalMonthlyCost: toNumber(item.totalMonthlyCost),
      annualImpact: toNumber(item.totalMonthlyCost) * 12,
      status: item.status,
    }));

    return [...currentRows, ...plannedRows];
  }, [activePeople, headcountPlan]);

  const plannedFutureRows = useMemo(
    () => financialRows.filter((row) => row.key.startsWith('plan-')),
    [financialRows],
  );

  const applyComputedCost = (grossValue, burdenValue) => {
    const gross = toNumber(grossValue);
    const burden = toNumber(burdenValue);
    if (!gross) return 0;
    if (!burden) return gross;
    return gross + (gross * burden / 100);
  };

  const updatePermissionMatrix = (currentRows, payload) => {
    const defaultRow = {
      personId: payload.personId,
      person: people.find((p) => p.id === Number(payload.personId))?.name || `Pessoa ${payload.personId}`,
      financeiro: EMPTY_PERMISSION_LEVEL,
      investidores: EMPTY_PERMISSION_LEVEL,
      marketing: EMPTY_PERMISSION_LEVEL,
      comercial: EMPTY_PERMISSION_LEVEL,
      projetos: EMPTY_PERMISSION_LEVEL,
      rh: EMPTY_PERMISSION_LEVEL,
      governanceAdmin: EMPTY_PERMISSION_LEVEL,
    };

    let found = false;
    const updated = currentRows.map((row) => {
      if (Number(row.personId) === Number(payload.personId)) {
        found = true;
        return { ...row, [payload.governanceArea]: payload.permissionLevel };
      }
      return row;
    });

    if (!found) {
      updated.push({ ...defaultRow, [payload.governanceArea]: payload.permissionLevel });
    }

    return updated;
  };

  const handleSaveProLabore = async (event) => {
    event.preventDefault();
    if (!founder) return;

    const payload = {
      monthlyValue: toNumber(proLaboreForm.monthlyValue),
      startMonth: proLaboreForm.startMonth,
      costCenter: proLaboreForm.costCenter,
      financialCategory: proLaboreForm.financialCategory,
      impactsFinancial: proLaboreForm.impactsFinancial,
      scenario: proLaboreForm.scenario,
      notes: proLaboreForm.notes,
    };

    await updateProLabore(founder.id, payload);

    setPeople((current) => current.map((person) => (
      person.id === founder.id
        ? {
          ...person,
          monthlyGrossCost: payload.monthlyValue,
          monthlyTotalCost: payload.monthlyValue,
          startDate: payload.startMonth,
          costCenter: payload.costCenter,
          financialCategory: payload.financialCategory,
          impactsFinancial: payload.impactsFinancial,
          projectionScenario: payload.scenario,
          notes: payload.notes,
        }
        : person
    )));

    setShowProLaboreForm(false);
  };

  const handleSavePerson = async (event) => {
    event.preventDefault();
    const totalCost = applyComputedCost(personForm.monthlyGrossCost, personForm.burdenRate);
    const payload = {
      ...personForm,
      id: Date.now(),
      secondaryAreas: personForm.secondaryAreas
        ? personForm.secondaryAreas.split(',').map((value) => value.trim()).filter(Boolean)
        : [],
      monthlyGrossCost: personForm.monthlyGrossCost === '' ? '' : toNumber(personForm.monthlyGrossCost),
      burdenRate: personForm.burdenRate === '' ? '' : toNumber(personForm.burdenRate),
      monthlyTotalCost: totalCost,
    };

    await savePerson(payload);
    setPeople((current) => [...current, payload]);
    setPersonForm(makeDefaultPerson());
    setShowPersonForm(false);
  };

  const handleSavePlan = async (event) => {
    event.preventDefault();
    const total = applyComputedCost(planForm.monthlyGrossCost, planForm.burdenRate);
    const payload = {
      ...planForm,
      id: Date.now(),
      monthlyGrossCost: planForm.monthlyGrossCost === '' ? '' : toNumber(planForm.monthlyGrossCost),
      burdenRate: planForm.burdenRate === '' ? '' : toNumber(planForm.burdenRate),
      totalMonthlyCost: total,
    };

    await saveHeadcountPlan(payload);
    setHeadcountPlan((current) => [...current, payload]);
    setPlanForm(makeDefaultPlan());
    setShowPlanForm(false);
  };

  const handleSavePermission = async (event) => {
    event.preventDefault();
    await savePermission(permissionForm);
    setPermissions((current) => updatePermissionMatrix(current, permissionForm));
    setPermissionForm(makeDefaultPermission());
    setShowPermissionForm(false);
  };

  const handleTabClick = (tabId) => {
    const target = TAB_ITEMS.find((tab) => tab.id === tabId);
    if (target) navigate(target.path);
  };

  const renderMetricCard = (label, value, Icon) => (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
        <Icon className="w-4 h-4" style={{ color: BRAND_GREEN }} />
      </div>
      <div className="text-xl font-extrabold text-gray-900">{value}</div>
    </div>
  );

  const renderOverview = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {renderMetricCard('Pessoas ativas', metrics.activePeople, Users)}
        {renderMetricCard('Pró-labore atual', currency(metrics.proLaboreAtual), Wallet)}
        {renderMetricCard('Custo mensal atual', currency(metrics.monthlyCurrentCost), Wallet)}
        {renderMetricCard('Contratações planejadas', metrics.plannedCount, Briefcase)}
        {renderMetricCard('Custo planejado futuro', currency(metrics.plannedMonthlyCost), CalendarClock)}
        {renderMetricCard('Áreas com responsável', metrics.coveredAreasText, LinkIcon)}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h3 className="text-lg font-bold text-gray-900">Pessoa inicial (registro real)</h3>
          <button
            type="button"
            onClick={() => setShowProLaboreForm((value) => !value)}
            className="px-3 py-2 rounded-md text-sm font-bold border border-gray-300 text-gray-700"
          >
            Alterar pró-labore
          </button>
        </div>

        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="py-2 pr-4">Pessoa</th>
                <th className="py-2 pr-4">Tipo</th>
                <th className="py-2 pr-4">Área</th>
                <th className="py-2 pr-4">Cargo</th>
                <th className="py-2 pr-4">Pró-labore atual</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Centro de custo</th>
                <th className="py-2 pr-4">Categoria financeira</th>
              </tr>
            </thead>
            <tbody>
              {activePeople.map((person) => (
                <tr key={person.id} className="border-b border-gray-100">
                  <td className="py-2 pr-4 font-semibold text-gray-900">{person.name}</td>
                  <td className="py-2 pr-4">{person.type}</td>
                  <td className="py-2 pr-4">{person.primaryArea}</td>
                  <td className="py-2 pr-4">{person.roleTitle}</td>
                  <td className="py-2 pr-4">{currency(person.monthlyGrossCost)}</td>
                  <td className="py-2 pr-4">{person.status}</td>
                  <td className="py-2 pr-4">{person.costCenter || '—'}</td>
                  <td className="py-2 pr-4">{person.financialCategory || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {showProLaboreForm && founder && (
          <form className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-3" onSubmit={handleSaveProLabore}>
            <label className="text-sm">
              <span className="text-gray-600">Valor mensal do pró-labore</span>
              <input type="number" min="0" step="0.01" className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={proLaboreForm.monthlyValue}
                onChange={(e) => setProLaboreForm((v) => ({ ...v, monthlyValue: e.target.value }))} />
            </label>
            <label className="text-sm">
              <span className="text-gray-600">Data de início</span>
              <input type="date" className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={proLaboreForm.startMonth}
                onChange={(e) => setProLaboreForm((v) => ({ ...v, startMonth: e.target.value }))} />
            </label>
            <label className="text-sm">
              <span className="text-gray-600">Centro de custo</span>
              <select className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={proLaboreForm.costCenter}
                onChange={(e) => setProLaboreForm((v) => ({ ...v, costCenter: e.target.value }))}>
                {RH_COST_CENTERS.map((option) => <option key={option} value={option}>{option}</option>)}
              </select>
            </label>
            <label className="text-sm">
              <span className="text-gray-600">Categoria financeira</span>
              <select className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={proLaboreForm.financialCategory}
                onChange={(e) => setProLaboreForm((v) => ({ ...v, financialCategory: e.target.value }))}>
                {RH_FINANCIAL_CATEGORIES.map((option) => <option key={option} value={option}>{option}</option>)}
              </select>
            </label>
            <label className="text-sm">
              <span className="text-gray-600">Impacta financeiro?</span>
              <select className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={proLaboreForm.impactsFinancial ? 'Sim' : 'Não'}
                onChange={(e) => setProLaboreForm((v) => ({ ...v, impactsFinancial: e.target.value === 'Sim' }))}>
                <option>Sim</option>
                <option>Não</option>
              </select>
            </label>
            <label className="text-sm">
              <span className="text-gray-600">Cenário financeiro</span>
              <select className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={proLaboreForm.scenario}
                onChange={(e) => setProLaboreForm((v) => ({ ...v, scenario: e.target.value }))}>
                {RH_SCENARIOS.map((option) => <option key={option} value={option}>{option}</option>)}
              </select>
            </label>
            <label className="text-sm md:col-span-2">
              <span className="text-gray-600">Observações</span>
              <textarea className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" rows={3} value={proLaboreForm.notes}
                onChange={(e) => setProLaboreForm((v) => ({ ...v, notes: e.target.value }))} />
            </label>
            <div className="md:col-span-2 flex justify-end gap-2">
              <button type="button" onClick={() => setShowProLaboreForm(false)} className="px-3 py-2 rounded-md border border-gray-300 text-sm font-semibold">Cancelar</button>
              <button type="submit" className="px-3 py-2 rounded-md text-sm font-bold text-white" style={{ backgroundColor: BRAND_GREEN }}>Salvar pró-labore</button>
            </div>
          </form>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-lg font-bold text-gray-900 mb-3">Planejamento próximo</h3>
        {headcountPlan.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 p-6 text-sm text-gray-500">
            Nenhuma contratação planejada cadastrada ainda.
          </div>
        ) : (
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-4">Cargo planejado</th>
                  <th className="py-2 pr-4">Área</th>
                  <th className="py-2 pr-4">Mês previsto</th>
                  <th className="py-2 pr-4">Cenário</th>
                  <th className="py-2 pr-4">Custo estimado</th>
                  <th className="py-2 pr-4">Prioridade</th>
                </tr>
              </thead>
              <tbody>
                {headcountPlan.map((item) => (
                  <tr key={item.id} className="border-b border-gray-100">
                    <td className="py-2 pr-4 font-semibold text-gray-900">{item.plannedRole}</td>
                    <td className="py-2 pr-4">{item.area}</td>
                    <td className="py-2 pr-4">{item.startMonth || '—'}</td>
                    <td className="py-2 pr-4">{item.scenario || '—'}</td>
                    <td className="py-2 pr-4">{currency(item.totalMonthlyCost)}</td>
                    <td className="py-2 pr-4">{item.priority || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderPeople = () => (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button type="button" onClick={() => setShowPersonForm((value) => !value)} className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-bold text-white" style={{ backgroundColor: BRAND_GREEN }}>
          <Plus className="w-4 h-4" />
          Incluir funcionário
        </button>
      </div>

      {showPersonForm && (
        <form onSubmit={handleSavePerson} className="bg-white rounded-xl border border-gray-200 p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-sm md:col-span-2"><span className="text-gray-600">Nome completo</span><input required className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={personForm.name} onChange={(e) => setPersonForm((v) => ({ ...v, name: e.target.value }))} /></label>
          <label className="text-sm"><span className="text-gray-600">E-mail</span><input type="email" className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={personForm.email} onChange={(e) => setPersonForm((v) => ({ ...v, email: e.target.value }))} /></label>
          <label className="text-sm"><span className="text-gray-600">Tipo de vínculo</span><select className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={personForm.type} onChange={(e) => setPersonForm((v) => ({ ...v, type: e.target.value }))}>{PERSON_TYPE_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}</select></label>
          <label className="text-sm"><span className="text-gray-600">Cargo</span><input required className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={personForm.roleTitle} onChange={(e) => setPersonForm((v) => ({ ...v, roleTitle: e.target.value }))} /></label>
          <label className="text-sm"><span className="text-gray-600">Área principal</span><select className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={personForm.primaryArea} onChange={(e) => setPersonForm((v) => ({ ...v, primaryArea: e.target.value }))}>{AREA_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}</select></label>
          <label className="text-sm md:col-span-2"><span className="text-gray-600">Áreas secundárias (separadas por vírgula)</span><input className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={personForm.secondaryAreas} onChange={(e) => setPersonForm((v) => ({ ...v, secondaryAreas: e.target.value }))} /></label>
          <label className="text-sm"><span className="text-gray-600">Data de início</span><input type="date" className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={personForm.startDate} onChange={(e) => setPersonForm((v) => ({ ...v, startDate: e.target.value }))} /></label>
          <label className="text-sm"><span className="text-gray-600">Status</span><select className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={personForm.status} onChange={(e) => setPersonForm((v) => ({ ...v, status: e.target.value }))}>{STATUS_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}</select></label>
          <label className="text-sm"><span className="text-gray-600">Regime de remuneração</span><select className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={personForm.regime} onChange={(e) => setPersonForm((v) => ({ ...v, regime: e.target.value }))}>{REGIME_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}</select></label>
          <label className="text-sm"><span className="text-gray-600">Remuneração mensal bruta</span><input type="number" min="0" step="0.01" className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={personForm.monthlyGrossCost} onChange={(e) => setPersonForm((v) => ({ ...v, monthlyGrossCost: e.target.value }))} /></label>
          <label className="text-sm"><span className="text-gray-600">Encargos estimados (%)</span><input type="number" min="0" step="0.01" className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={personForm.burdenRate} onChange={(e) => setPersonForm((v) => ({ ...v, burdenRate: e.target.value }))} /></label>
          <label className="text-sm"><span className="text-gray-600">Custo total mensal</span><input disabled className="mt-1 w-full border border-gray-200 rounded-md px-3 py-2 bg-gray-50 text-gray-700" value={currency(applyComputedCost(personForm.monthlyGrossCost, personForm.burdenRate))} /></label>
          <label className="text-sm"><span className="text-gray-600">Centro de custo</span><select className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={personForm.costCenter} onChange={(e) => setPersonForm((v) => ({ ...v, costCenter: e.target.value }))}>{RH_COST_CENTERS.map((option) => <option key={option} value={option}>{option}</option>)}</select></label>
          <label className="text-sm"><span className="text-gray-600">Categoria financeira</span><select className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={personForm.financialCategory} onChange={(e) => setPersonForm((v) => ({ ...v, financialCategory: e.target.value }))}>{RH_FINANCIAL_CATEGORIES.map((option) => <option key={option} value={option}>{option}</option>)}</select></label>
          <label className="text-sm"><span className="text-gray-600">Cenário financeiro</span><select className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={personForm.projectionScenario} onChange={(e) => setPersonForm((v) => ({ ...v, projectionScenario: e.target.value }))}>{RH_SCENARIOS.map((option) => <option key={option} value={option}>{option}</option>)}</select></label>
          <label className="text-sm"><span className="text-gray-600">Impacta projeções financeiras?</span><select className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={personForm.impactsFinancial ? 'Sim' : 'Não'} onChange={(e) => setPersonForm((v) => ({ ...v, impactsFinancial: e.target.value === 'Sim' }))}><option>Sim</option><option>Não</option></select></label>
          <label className="text-sm md:col-span-2"><span className="text-gray-600">Observações</span><textarea rows={3} className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={personForm.notes} onChange={(e) => setPersonForm((v) => ({ ...v, notes: e.target.value }))} /></label>
          <div className="md:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={() => setShowPersonForm(false)} className="px-3 py-2 rounded-md border border-gray-300 text-sm font-semibold">Cancelar</button>
            <button type="submit" className="px-3 py-2 rounded-md text-sm font-bold text-white" style={{ backgroundColor: BRAND_GREEN }}>Salvar funcionário</button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-5 overflow-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b border-gray-200">
              <th className="py-2 pr-4">Nome</th>
              <th className="py-2 pr-4">Tipo</th>
              <th className="py-2 pr-4">Cargo</th>
              <th className="py-2 pr-4">Área principal</th>
              <th className="py-2 pr-4">Regime</th>
              <th className="py-2 pr-4">Data de início</th>
              <th className="py-2 pr-4">Custo mensal</th>
              <th className="py-2 pr-4">Status</th>
              <th className="py-2 pr-4">Ações</th>
            </tr>
          </thead>
          <tbody>
            {people.map((person) => (
              <tr key={person.id} className="border-b border-gray-100">
                <td className="py-2 pr-4 font-semibold text-gray-900">{person.name}</td>
                <td className="py-2 pr-4">{person.type}</td>
                <td className="py-2 pr-4">{person.roleTitle}</td>
                <td className="py-2 pr-4">{person.primaryArea || '—'}</td>
                <td className="py-2 pr-4">{person.regime || '—'}</td>
                <td className="py-2 pr-4">{person.startDate || '—'}</td>
                <td className="py-2 pr-4">{currency(person.monthlyTotalCost || person.monthlyGrossCost)}</td>
                <td className="py-2 pr-4">{person.status}</td>
                <td className="py-2 pr-4">
                  <button className="px-3 py-1 text-xs font-bold rounded-md border border-gray-300 text-gray-700" type="button" onClick={async () => { await updatePerson(person.id, person); }}>
                    Editar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderCompensation = () => (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h3 className="text-lg font-bold text-gray-900">Pró-labore do fundador</h3>
          <button
            type="button"
            onClick={() => setShowProLaboreForm((value) => !value)}
            className="px-3 py-2 rounded-md text-sm font-bold border border-gray-300 text-gray-700"
          >
            Alterar pró-labore
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
          <div><div className="text-gray-500">Pessoa</div><div className="font-semibold">{founder?.name || '—'}</div></div>
          <div><div className="text-gray-500">Valor mensal</div><div className="font-semibold">{currency(founder?.monthlyGrossCost)}</div></div>
          <div><div className="text-gray-500">Início</div><div className="font-semibold">{founder?.startDate || '—'}</div></div>
          <div><div className="text-gray-500">Cenário</div><div className="font-semibold">{founder?.projectionScenario || 'Não definido'}</div></div>
        </div>
        <p className="text-xs text-gray-500 mt-3">Observações: {founder?.notes || '—'}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h4 className="font-bold text-gray-900 mb-2">Salários atuais</h4>
          {people.filter((p) => p.status === 'Ativo' && p.regime !== 'Pró-labore').length === 0 ? (
            <div className="text-sm text-gray-500">Nenhum salário cadastrado ainda.</div>
          ) : people.filter((p) => p.status === 'Ativo' && p.regime !== 'Pró-labore').map((item) => (
            <div key={item.id} className="text-sm py-2 border-b border-gray-100 last:border-0">
              <div className="font-semibold text-gray-900">{item.name}</div>
              <div className="text-gray-600">{item.primaryArea || '—'} · {currency(item.monthlyTotalCost || item.monthlyGrossCost)} / mês</div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h4 className="font-bold text-gray-900 mb-2">Prestadores / PJ</h4>
          {people.filter((p) => p.regime === 'PJ').length === 0 ? (
            <div className="text-sm text-gray-500">Nenhum prestador PJ cadastrado ainda.</div>
          ) : people.filter((p) => p.regime === 'PJ').map((item) => (
            <div key={item.id} className="text-sm py-2 border-b border-gray-100 last:border-0">
              <div className="font-semibold text-gray-900">{item.name}</div>
              <div className="text-gray-600">{currency(item.monthlyTotalCost || item.monthlyGrossCost)} / mês</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h4 className="font-bold text-gray-900 mb-2">Encargos estimados</h4>
          <div className="text-sm text-gray-500">Sem encargos cadastrados ainda.</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h4 className="font-bold text-gray-900 mb-2">Benefícios</h4>
          <div className="text-sm text-gray-500">Sem benefícios cadastrados ainda.</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h4 className="font-bold text-gray-900 mb-2">Equity / Stock options</h4>
          <p className="text-sm text-gray-600">Campo disponível para registro futuro, sem impacto imediato no caixa.</p>
        </div>
      </div>
    </div>
  );

  const renderHeadcountPlan = () => (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button type="button" onClick={() => setShowPlanForm((value) => !value)} className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-bold text-white" style={{ backgroundColor: BRAND_GREEN }}>
          <Plus className="w-4 h-4" />
          Incluir cargo planejado
        </button>
      </div>

      {showPlanForm && (
        <form onSubmit={handleSavePlan} className="bg-white rounded-xl border border-gray-200 p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-sm"><span className="text-gray-600">Cargo planejado</span><input required className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={planForm.plannedRole} onChange={(e) => setPlanForm((v) => ({ ...v, plannedRole: e.target.value }))} /></label>
          <label className="text-sm"><span className="text-gray-600">Área</span><select className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={planForm.area} onChange={(e) => setPlanForm((v) => ({ ...v, area: e.target.value }))}>{AREA_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}</select></label>
          <label className="text-sm"><span className="text-gray-600">Mês previsto de entrada</span><input type="month" className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={planForm.startMonth} onChange={(e) => setPlanForm((v) => ({ ...v, startMonth: e.target.value }))} /></label>
          <label className="text-sm"><span className="text-gray-600">Cenário financeiro</span><select className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={planForm.scenario} onChange={(e) => setPlanForm((v) => ({ ...v, scenario: e.target.value }))}>{RH_SCENARIOS.map((option) => <option key={option} value={option}>{option}</option>)}</select></label>
          <label className="text-sm"><span className="text-gray-600">Custo mensal bruto previsto</span><input type="number" min="0" step="0.01" className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={planForm.monthlyGrossCost} onChange={(e) => setPlanForm((v) => ({ ...v, monthlyGrossCost: e.target.value }))} /></label>
          <label className="text-sm"><span className="text-gray-600">Encargos estimados (%)</span><input type="number" min="0" step="0.01" className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={planForm.burdenRate} onChange={(e) => setPlanForm((v) => ({ ...v, burdenRate: e.target.value }))} /></label>
          <label className="text-sm"><span className="text-gray-600">Custo total mensal previsto</span><input disabled className="mt-1 w-full border border-gray-200 rounded-md px-3 py-2 bg-gray-50" value={currency(applyComputedCost(planForm.monthlyGrossCost, planForm.burdenRate))} /></label>
          <label className="text-sm"><span className="text-gray-600">Centro de custo</span><select className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={planForm.costCenter} onChange={(e) => setPlanForm((v) => ({ ...v, costCenter: e.target.value }))}>{RH_COST_CENTERS.map((option) => <option key={option} value={option}>{option}</option>)}</select></label>
          <label className="text-sm"><span className="text-gray-600">Categoria financeira</span><select className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={planForm.financialCategory} onChange={(e) => setPlanForm((v) => ({ ...v, financialCategory: e.target.value }))}>{RH_FINANCIAL_CATEGORIES.map((option) => <option key={option} value={option}>{option}</option>)}</select></label>
          <label className="text-sm"><span className="text-gray-600">Prioridade</span><select className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={planForm.priority} onChange={(e) => setPlanForm((v) => ({ ...v, priority: e.target.value }))}><option>Alta</option><option>Média</option><option>Baixa</option></select></label>
          <label className="text-sm"><span className="text-gray-600">Dependência</span><input className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" placeholder="investimento, receita, contrato..." value={planForm.dependency} onChange={(e) => setPlanForm((v) => ({ ...v, dependency: e.target.value }))} /></label>
          <label className="text-sm"><span className="text-gray-600">Status</span><select className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={planForm.status} onChange={(e) => setPlanForm((v) => ({ ...v, status: e.target.value }))}><option>Planejado</option><option>Aprovado</option><option>Contratado</option><option>Cancelado</option></select></label>
          <label className="text-sm md:col-span-2"><span className="text-gray-600">Observações</span><textarea rows={3} className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={planForm.notes} onChange={(e) => setPlanForm((v) => ({ ...v, notes: e.target.value }))} /></label>
          <div className="md:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={() => setShowPlanForm(false)} className="px-3 py-2 rounded-md border border-gray-300 text-sm font-semibold">Cancelar</button>
            <button type="submit" className="px-3 py-2 rounded-md text-sm font-bold text-white" style={{ backgroundColor: BRAND_GREEN }}>Salvar planejamento</button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-5 overflow-auto">
        {headcountPlan.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 p-6 text-sm text-gray-500">Nenhuma contratação planejada cadastrada ainda.</div>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="py-2 pr-4">Cargo planejado</th>
                <th className="py-2 pr-4">Área</th>
                <th className="py-2 pr-4">Mês de entrada</th>
                <th className="py-2 pr-4">Cenário</th>
                <th className="py-2 pr-4">Custo mensal bruto</th>
                <th className="py-2 pr-4">Encargos estimados</th>
                <th className="py-2 pr-4">Custo total mensal</th>
                <th className="py-2 pr-4">Prioridade</th>
                <th className="py-2 pr-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {headcountPlan.map((item) => (
                <tr key={item.id} className="border-b border-gray-100">
                  <td className="py-2 pr-4 font-semibold text-gray-900">{item.plannedRole}</td>
                  <td className="py-2 pr-4">{item.area}</td>
                  <td className="py-2 pr-4">{item.startMonth || '—'}</td>
                  <td className="py-2 pr-4">{item.scenario || '—'}</td>
                  <td className="py-2 pr-4">{item.monthlyGrossCost === '' ? '—' : currency(item.monthlyGrossCost)}</td>
                  <td className="py-2 pr-4">{item.burdenRate === '' ? '—' : `${item.burdenRate}%`}</td>
                  <td className="py-2 pr-4">{currency(item.totalMonthlyCost)}</td>
                  <td className="py-2 pr-4">{item.priority}</td>
                  <td className="py-2 pr-4">{item.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );

  const renderPermissions = () => (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button type="button" onClick={() => setShowPermissionForm((value) => !value)} className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-bold text-white" style={{ backgroundColor: BRAND_GREEN }}>
          <Plus className="w-4 h-4" />
          Definir permissão
        </button>
      </div>

      {showPermissionForm && (
        <form onSubmit={handleSavePermission} className="bg-white rounded-xl border border-gray-200 p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-sm"><span className="text-gray-600">Pessoa</span><select className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={permissionForm.personId} onChange={(e) => setPermissionForm((v) => ({ ...v, personId: Number(e.target.value) }))}>{people.map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}</select></label>
          <label className="text-sm"><span className="text-gray-600">Área da Governança</span><select className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={permissionForm.governanceArea} onChange={(e) => setPermissionForm((v) => ({ ...v, governanceArea: e.target.value }))}>{GOVERNANCE_AREAS.map((area) => <option key={area.key} value={area.key}>{area.label}</option>)}</select></label>
          <label className="text-sm"><span className="text-gray-600">Nível de acesso</span><select className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={permissionForm.permissionLevel} onChange={(e) => setPermissionForm((v) => ({ ...v, permissionLevel: e.target.value }))}><option>Sem acesso</option><option>Leitor</option><option>Editor</option><option>Admin</option></select></label>
          <label className="text-sm"><span className="text-gray-600">Observações</span><input className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={permissionForm.notes} onChange={(e) => setPermissionForm((v) => ({ ...v, notes: e.target.value }))} /></label>
          <div className="md:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={() => setShowPermissionForm(false)} className="px-3 py-2 rounded-md border border-gray-300 text-sm font-semibold">Cancelar</button>
            <button type="submit" className="px-3 py-2 rounded-md text-sm font-bold text-white" style={{ backgroundColor: BRAND_GREEN }}>Salvar permissão</button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-5 overflow-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b border-gray-200">
              <th className="py-2 pr-4">Pessoa</th>
              <th className="py-2 pr-4">Financeiro</th>
              <th className="py-2 pr-4">Investidores</th>
              <th className="py-2 pr-4">Marketing</th>
              <th className="py-2 pr-4">Comercial</th>
              <th className="py-2 pr-4">Projetos</th>
              <th className="py-2 pr-4">RH</th>
              <th className="py-2 pr-4">Governança Admin</th>
            </tr>
          </thead>
          <tbody>
            {permissions.map((item) => (
              <tr key={item.person} className="border-b border-gray-100">
                <td className="py-2 pr-4 font-semibold text-gray-900">{item.person}</td>
                <td className="py-2 pr-4">{item.financeiro || EMPTY_PERMISSION_LEVEL}</td>
                <td className="py-2 pr-4">{item.investidores || EMPTY_PERMISSION_LEVEL}</td>
                <td className="py-2 pr-4">{item.marketing || EMPTY_PERMISSION_LEVEL}</td>
                <td className="py-2 pr-4">{item.comercial || EMPTY_PERMISSION_LEVEL}</td>
                <td className="py-2 pr-4">{item.projetos || EMPTY_PERMISSION_LEVEL}</td>
                <td className="py-2 pr-4">{item.rh || EMPTY_PERMISSION_LEVEL}</td>
                <td className="py-2 pr-4">{item.governanceAdmin || EMPTY_PERMISSION_LEVEL}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-gray-500 mt-3">
          Escopo desta matriz: permissões da Governança interna (ops.vukz.ai). Não se aplica a permissões do app cliente.
        </p>
      </div>
    </div>
  );

  const renderFinancialIntegration = () => (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-lg font-bold text-gray-900 mb-2">Como RH alimenta o Financeiro</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-700">
          <div>• Pró-labore → DRE / Despesas administrativas</div>
          <div>• Produto/Tecnologia → DRE / Produto ou Tecnologia</div>
          <div>• IA/Dados → DRE / Pesquisa e Desenvolvimento</div>
          <div>• Comercial → DRE / Vendas</div>
          <div>• Marketing → DRE / Marketing</div>
          <div>• Customer Success → DRE / Operações</div>
          <div>• Encargos → DRE / Encargos sobre folha</div>
          <div>• Contratações planejadas → Projeção de fluxo de caixa</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="text-sm text-gray-500">Total atual de RH</div>
          <div className="text-2xl font-extrabold text-gray-900">{currency(financialRows.filter((row) => row.status === 'Ativo').reduce((acc, row) => acc + toNumber(row.totalMonthlyCost), 0))}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="text-sm text-gray-500">Total planejado</div>
          <div className="text-2xl font-extrabold text-gray-900">{currency(plannedFutureRows.reduce((acc, row) => acc + toNumber(row.totalMonthlyCost), 0))}</div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5 overflow-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b border-gray-200">
              <th className="py-2 pr-4">Pessoa/Cargo</th>
              <th className="py-2 pr-4">Centro de custo</th>
              <th className="py-2 pr-4">Categoria financeira</th>
              <th className="py-2 pr-4">Cenário</th>
              <th className="py-2 pr-4">Mês inicial</th>
              <th className="py-2 pr-4">Custo mensal total</th>
              <th className="py-2 pr-4">Impacto anual</th>
              <th className="py-2 pr-4">Status</th>
            </tr>
          </thead>
          <tbody>
            {financialRows.map((item) => (
              <tr key={item.key} className="border-b border-gray-100">
                <td className="py-2 pr-4 font-semibold text-gray-900">{item.personOrRole}</td>
                <td className="py-2 pr-4">{item.costCenter || '—'}</td>
                <td className="py-2 pr-4">{item.financialCategory || '—'}</td>
                <td className="py-2 pr-4">{item.projectionScenario || '—'}</td>
                <td className="py-2 pr-4">{item.startMonth || '—'}</td>
                <td className="py-2 pr-4">{currency(item.totalMonthlyCost)}</td>
                <td className="py-2 pr-4">{currency(item.annualImpact)}</td>
                <td className="py-2 pr-4">{item.status || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {plannedFutureRows.length === 0 && (
          <div className="mt-4 rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-500">
            Nenhum custo futuro de RH cadastrado ainda.
          </div>
        )}
      </div>
    </div>
  );

  const renderContent = () => {
    if (loading) {
      return <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">{t.loading}</div>;
    }

    if (activeTab === 'people') return renderPeople();
    if (activeTab === 'compensation') return renderCompensation();
    if (activeTab === 'headcount-plan') return renderHeadcountPlan();
    if (activeTab === 'permissions') return renderPermissions();
    if (activeTab === 'financial-integration') return renderFinancialIntegration();

    return renderOverview();
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border p-6" style={{ borderColor: BRAND_GREEN_LIGHT, backgroundColor: BRAND_WHITE }}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <img src="/vukz-logo.png" alt="Vukz" className="w-11 h-11 rounded-xl object-contain bg-gray-50 border border-gray-200 p-0.5" onError={(e) => { e.target.style.display = 'none'; }} />
            <div>
            <h1 className="text-gray-900 text-2xl font-extrabold">{t.title}</h1>
            <p className="text-sm text-gray-600 mt-1">
              {t.subtitle}
            </p>
            </div>
          </div>
          <div
            className="px-3 py-1 rounded-full text-xs font-bold border"
            style={{ backgroundColor: BRAND_GREEN_LIGHT, color: BRAND_GREEN, borderColor: BRAND_GOLD }}
          >
            {t.badge}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-2 overflow-auto">
        <div className="flex gap-2 min-w-max">
          {TAB_ITEMS.map((tab) => {
            const isActive = tab.id === activeTab;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabClick(tab.id)}
                className="px-4 py-2 rounded-lg text-sm font-bold transition-colors"
                style={{
                  backgroundColor: isActive ? BRAND_GREEN : '#f3f4f6',
                  color: isActive ? BRAND_WHITE : '#374151',
                  border: `1px solid ${isActive ? BRAND_GREEN : '#e5e7eb'}`,
                }}
              >
                {t.tabLabels[tab.id] || tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {renderContent()}

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="font-bold text-gray-900 mb-2">Pronto para integração backend</h3>
        <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
          <li>GET /api/governance/rh/people</li>
          <li>POST /api/governance/rh/people</li>
          <li>PATCH /api/governance/rh/people/:id</li>
          <li>PATCH /api/governance/rh/people/:id/pro-labore</li>
          <li>GET /api/governance/rh/headcount-plan</li>
          <li>POST /api/governance/rh/headcount-plan</li>
          <li>PATCH /api/governance/rh/headcount-plan/:id</li>
          <li>GET /api/governance/rh/permissions</li>
          <li>POST /api/governance/rh/permissions</li>
          <li>PATCH /api/governance/rh/permissions/:id</li>
          <li>GET /api/governance/rh/financial-projection</li>
        </ul>
      </div>
    </div>
  );
};

export default GovernanceRHPage;
